import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Avatar from '@mui/material/Avatar';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import LogoutIcon from '@mui/icons-material/Logout';
import TranslateIcon from '@mui/icons-material/Translate';
import CheckIcon from '@mui/icons-material/Check';
import HandymanIcon from '@mui/icons-material/Handyman';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useThemeMode } from '../App';
import { setLanguage } from '../i18n';

const LANGS = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'हिंदी' },
  { code: 'te', label: 'తెలుగు' },
];

const Layout = ({ children }) => {
  const { t, i18n } = useTranslation();
  const { user, updateUser, logout } = useAuth();
  const { mode, toggle } = useThemeMode();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(null);

  const changeLang = async (code) => {
    setAnchor(null);
    setLanguage(code);
    if (user) {
      updateUser({ ...user, language: code });
      try {
        await api.patch('/api/users/me', { language: code });
      } catch {
        /* offline is fine — preference is already applied locally */
      }
    }
  };

  const onLogout = async () => {
    setAnchor(null);
    await logout();
    navigate('/login');
  };

  return (
    <Box sx={{ minHeight: '100vh', pb: 6 }}>
      <AppBar
        position="sticky"
        elevation={0}
        color="transparent"
        sx={(theme) => ({
          backdropFilter: 'blur(12px)',
          backgroundColor:
            theme.palette.mode === 'dark' ? 'rgba(15,18,20,0.85)' : 'rgba(245,246,247,0.85)',
          borderBottom: `1px solid ${theme.palette.divider}`,
        })}
      >
        <Toolbar sx={{ gap: 1 }}>
          <Box
            onClick={() => navigate('/')}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', flexGrow: 1 }}
          >
            <Avatar sx={{ bgcolor: 'primary.main', width: 34, height: 34 }}>
              <HandymanIcon sx={{ fontSize: 20, color: 'primary.contrastText' }} />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
              {t('appName')}
            </Typography>
          </Box>

          <IconButton onClick={toggle} aria-label={t('common.darkMode')}>
            {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
          <IconButton onClick={(e) => setAnchor(e.currentTarget)} aria-label={t('common.language')}>
            <TranslateIcon />
          </IconButton>
          <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
            {LANGS.map((l) => (
              <MenuItem key={l.code} onClick={() => changeLang(l.code)}>
                <ListItemIcon>{i18n.language === l.code && <CheckIcon fontSize="small" />}</ListItemIcon>
                <ListItemText>{l.label}</ListItemText>
              </MenuItem>
            ))}
            <Divider />
            <MenuItem onClick={onLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('common.logout')}</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ pt: 3 }}>
        {children}
      </Container>
    </Box>
  );
};

export default Layout;
